/* eslint-disable no-param-reassign */
/* eslint-disable dot-notation */
import { FormatUrlParameters } from '@garmin/support-utils';
// eslint-disable-next-line import/no-cycle
import { capi } from '../../client';
import ApiConfig from '../../../../config/api';

const getDefaultState = () => ({
  topFaqs: null,
  textResults: false,
  textResultsPrivate: false,
  videoResults: false,
});

export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    topFaqs: (state) => state.topFaqs,
    textResults: (state) => state.textResults,
    textResultsPrivate: (state) => state.textResultsPrivate,
    videoResults: (state) => state.videoResults,
    // eslint-disable-next-line max-len
    getTextResultsUrlQs: (state, getters, rootState, rootGetters) => (queryParamsObj, isCTArticle) => (
      new URLSearchParams({
        ...FormatUrlParameters.stripFalsyProps(queryParamsObj),
        ct: isCTArticle ? 'article' : 'content',
        mr: queryParamsObj.resultsPerPage || ApiConfig.textResultsPerPage,
        locale: queryParamsObj.altLocale || rootGetters.locale,
        // eslint-disable-next-line max-len
        ...queryParamsObj.searchQuery && { st: queryParamsObj.searchQuery.substring(0, ApiConfig.searchQueryMaxLength) },
        // eslint-disable-next-line max-len
        si: queryParamsObj.startIndex || (queryParamsObj.textPage || 0) * ApiConfig.textResultsPerPage,
        tags: [
          queryParamsObj.productTagName || null,
          queryParamsObj.topicTag || null,
          queryParamsObj.tabTagName || null,
          queryParamsObj.tab === 'welcome' ? ApiConfig.welcomeContentTag : '',
        ],
      }).toString()
    ),
    getVideoResultsUrlQs: (state, getters, rootState, rootGetters) => (queryParamsObj) => (
      new URLSearchParams({
        ...FormatUrlParameters.stripFalsyProps(queryParamsObj),
        ct: 'video',
        mr: queryParamsObj.resultsPerPage || ApiConfig.videoResultsPerPage,
        locale: queryParamsObj.altLocale || rootGetters.locale,
        ...queryParamsObj.searchQuery && { st: queryParamsObj.searchQuery.substring(0, ApiConfig.searchQueryMaxLength) },
        ...(queryParamsObj.tab === 'videos') && { tagPrioritySort: 'region_featuredvideo' },
        si: queryParamsObj.startIndex || (queryParamsObj.videoPage || 0) * ApiConfig.videoResultsPerPage,
        tags: [
          queryParamsObj.productTagName || null,
          queryParamsObj.topicTag || null,
          queryParamsObj.tabTagName || null,
          queryParamsObj.tab === 'welcome' ? ApiConfig.welcomeContentTag : '',
        ],
      }).toString()
    ),
  },
  mutations: {
    reset(state) {
      Object.assign(state, getDefaultState());
    },
    topFaqsUpdate(state, faqs) {
      state.topFaqs = faqs;
    },
    textResultsUpdate(state, textResults) {
      state.textResults = textResults;
    },
    textResultsPrivateUpdate(state, textResultsPrivate) {
      state.textResultsPrivate = textResultsPrivate;
    },
    videoResultsUpdate(state, videoResults) {
      state.videoResults = videoResults;
    },
  },

  actions: {
    reset({ commit }) {
      commit('reset');
    },
    async getTopFaqs({ getters, commit, rootGetters }) {
      const apiId = 'getTopFaqs';
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      if (new URLSearchParams(window.location.search).has('topicTag') || !(rootGetters['product'] || {}).tagName) return;

      commit('incrementApiCallsInProgress', apiId, { root: true });

      try {
        const endpointPath = `content/${rootGetters.locale}/?${
          getters.getTextResultsUrlQs({ ...rootGetters.apiParams, topicTag: 'region_consumertopfaq' })}`;
        const { data } = await capi.get(endpointPath);

        if (getters.topFaqs && rootGetters.urlParams.textPage > 0) {
          const combinedResults = { ...getters.topFaqs };

          if (combinedResults.contentDetails) {
            // eslint-disable-next-line max-len
            combinedResults.contentDetails = combinedResults.contentDetails.concat(data.contentDetails);
          }

          commit('topFaqsUpdate', { ...combinedResults });
        } else {
          commit('topFaqsUpdate', { ...data });
        }
      } catch (e) {
        commit('topFaqsUpdate', null);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
    async fetchTextResults({ getters, commit, rootGetters }) {
      const apiId = 'fetchTextResults';
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      commit('incrementApiCallsInProgress', apiId, { root: true });

      try {
        const params = rootGetters.apiParams;
        const locale = params.altLocale || rootGetters.locale;
        const endpointPath = `content/${locale}/?${getters.getTextResultsUrlQs(params)}`;

        const { data, status } = await capi.get(endpointPath);

        if (data && status === 200) {
          if (!data?.contentDetails) data.contentDetails = [];

          if (rootGetters.urlParams.textPage > 0) {
            const combinedResults = { ...getters.textResults };

            if (combinedResults?.contentDetails) {
              // eslint-disable-next-line max-len
              combinedResults.contentDetails = combinedResults.contentDetails.concat(data.contentDetails);
            }
            commit('textResultsUpdate', combinedResults);
          } else {
            commit('textResultsUpdate', data);
          }
        }

        if (data.suggestedSearch) {
          commit('urlParamsUpdate', { ...rootGetters.urlParams, searchQuery: data.suggestedSearch }, { root: true });
        }

        commit('isHasSearchedFlagUpdate', true, { root: true });
        commit('lastSearchedTimestampUpdate', Date.now(), { root: true });
      } catch (e) {
        commit('textResultsUpdate', getDefaultState().textResults);
        commit('isHasSearchedFlagUpdate', true, { root: true });
        commit('lastSearchedTimestampUpdate', Date.now(), { root: true });
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
    async fetchTextResultsPrivate({ getters, commit, rootGetters }) {
      const apiId = 'fetchTextResultsPrivate';
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      commit('incrementApiCallsInProgress', apiId, { root: true });

      const params = { ...rootGetters.apiParams };
      const locale = params.altLocale || rootGetters.locale;

      try {
        const endpointPath = `contentInternal/${locale}/?${getters.getTextResultsUrlQs({ ...params }, true)}`;
        const { data, status } = await capi.get(endpointPath);

        if (data && status === 200) {
          if (!data?.contentDetails) data.contentDetails = [];

          if (rootGetters.urlParams.textPage > 0) {
            const combinedResults = { ...getters.textResultsPrivate };

            if (combinedResults?.contentDetails) {
              // eslint-disable-next-line max-len
              combinedResults.contentDetails = combinedResults.contentDetails.concat(data.contentDetails);
            }
            commit('textResultsPrivateUpdate', combinedResults);
          } else {
            commit('textResultsPrivateUpdate', data);
          }
        }
      } catch (e) {
        commit('textResultsPrivateUpdate', null);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
    async fetchVideoResults({ commit, getters, rootGetters }) {
      const apiId = 'fetchVideoResults';
      if (rootGetters[`isApiIdCallInProgress(${apiId})`]) return;
      commit('incrementApiCallsInProgress', apiId, { root: true });

      try {
        const params = rootGetters.apiParams;
        const locale = params.altLocale || rootGetters.locale;
        if (params.tab === 'videos') params.resultsPerPage = ApiConfig.videoTabResultsPerPage;
        const endpointPath = `content/${locale}/getVideoResults?${getters.getVideoResultsUrlQs(params)}`;

        const { data, status } = await capi.get(endpointPath);

        if (data && status === 200) {
          if (!data?.contentDetails) data.contentDetails = [];

          if (rootGetters.urlParams.videoPage > 0) {
            const combinedResults = { ...getters.videoResults };

            if (combinedResults?.contentDetails) {
              // eslint-disable-next-line max-len
              combinedResults.contentDetails = combinedResults.contentDetails.concat(data.contentDetails);
            }
            commit('videoResultsUpdate', combinedResults);
          } else {
            commit('videoResultsUpdate', data);
          }
        }

        if (data.suggestedSearch) {
          commit('urlParamsUpdate', { ...rootGetters.urlParams, searchQuery: data.suggestedSearch }, { root: true });
        }
      } catch (e) {
        commit('videoResultsUpdate', getDefaultState().videoResults);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
  },
};
